import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { useGit } from '@/hooks/useGit';

export interface RepositoryInfo {
  naddr: string;
  identifier: string;
  pubkey: string;
  name?: string;
  description?: string;
  cloneUrls: string[];
  webUrls: string[];
  relays: string[];
  event: NostrEvent;
}

/**
 * Hook to fetch and manage the Nostr repository announcement for a project.
 * Returns the repository info if the project is connected to a Nostr git repository.
 */
export function useProjectRepo(projectId: string) {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const projectsManager = useProjectsManager();
  const { user } = useCurrentUser();
  const { git } = useGit();
  const queryClient = useQueryClient();

  const projectPath = `/projects/${projectId}`;

  // Query for the user's own repository announcement for this project
  const ownRepoQuery = useQuery<RepositoryInfo | null>({
    queryKey: ['project-repo-own', projectId, user?.pubkey],
    enabled: !!user?.pubkey,
    queryFn: async ({ signal }) => {
      if (!user?.pubkey) return null;

      try {
        const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);
        
        // Query for the user's own repo announcement with this project ID as identifier
        const events = await nostr
          .group(gitRelays)
          .query(
            [{
              kinds: [30617],
              authors: [user.pubkey],
              '#d': [projectId],
              limit: 1,
            }],
            { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
          );

        if (events.length === 0) return null;

        const event = events[0];

        // Extract tags
        const name = event.tags.find(([t]) => t === 'name')?.[1];
        const description = event.tags.find(([t]) => t === 'description')?.[1];
        const cloneUrls = event.tags.filter(([t]) => t === 'clone').map(([, url]) => url);
        const webUrls = event.tags.filter(([t]) => t === 'web').map(([, url]) => url);
        const relays = event.tags.filter(([t]) => t === 'relays').map(([, url]) => url);

        const naddr = nip19.naddrEncode({
          identifier: projectId,
          pubkey: user.pubkey,
          kind: 30617,
          relays: relays.length > 0 ? relays : undefined,
        });

        return {
          naddr,
          identifier: projectId,
          pubkey: user.pubkey,
          name,
          description,
          cloneUrls,
          webUrls,
          relays,
          event,
        };
      } catch {
        return null;
      }
    },
  });

  // Query for the original repository announcement (from git config)
  const originalRepoQuery = useQuery<RepositoryInfo | null>({
    queryKey: ['project-repo-original', projectId],
    queryFn: async ({ signal }) => {
      // Get the naddr from git config
      const naddr = await projectsManager.getNostrRepoAddress(projectId);
      if (!naddr) return null;

      try {
        // Decode the naddr to get the repository coordinates
        const decoded = nip19.decode(naddr);
        if (decoded.type !== 'naddr') return null;

        const { identifier, pubkey, kind } = decoded.data;
        if (kind !== 30617) return null;

        // Query for the repository announcement event
        const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);
        
        const events = await nostr
          .group(gitRelays)
          .query(
            [{
              kinds: [30617],
              authors: [pubkey],
              '#d': [identifier],
              limit: 1,
            }],
            { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) }
          );

        if (events.length === 0) return null;

        const event = events[0];

        // Extract tags
        const name = event.tags.find(([t]) => t === 'name')?.[1];
        const description = event.tags.find(([t]) => t === 'description')?.[1];
        const cloneUrls = event.tags.filter(([t]) => t === 'clone').map(([, url]) => url);
        const webUrls = event.tags.filter(([t]) => t === 'web').map(([, url]) => url);
        const relays = event.tags.filter(([t]) => t === 'relays').map(([, url]) => url);

        return {
          naddr,
          identifier,
          pubkey,
          name,
          description,
          cloneUrls,
          webUrls,
          relays,
          event,
        };
      } catch {
        return null;
      }
    },
  });

  // Prefer user's own repo, fallback to original
  const repoInfo = ownRepoQuery.data || originalRepoQuery.data;
  const isOwnRepo = ownRepoQuery.data !== null || (originalRepoQuery.data?.pubkey === user?.pubkey);

  const updateMutation = useMutation({
    mutationFn: async (updates: { name?: string; description?: string }) => {
      if (!user?.signer) {
        throw new Error('You must be logged in to update repository info');
      }

      const gitRelays = config.ngitServers.map((server) => `wss://${server}/`);
      const defaultRelays = ['wss://relay.nostr.band'];

      // Build tags for the event
      const newTags: string[][] = [];
      
      // Always include the d tag with projectId as identifier
      newTags.push(['d', projectId]);

      // Add name
      const nameValue = updates.name ?? ownRepoQuery.data?.name ?? originalRepoQuery.data?.name ?? projectId;
      if (nameValue.trim()) {
        newTags.push(['name', nameValue.trim()]);
      }

      // Add description
      const descValue = updates.description ?? ownRepoQuery.data?.description ?? originalRepoQuery.data?.description;
      if (descValue?.trim()) {
        newTags.push(['description', descValue.trim()]);
      }

      // If we have an existing own repo, preserve its other tags
      if (ownRepoQuery.data?.event) {
        for (const tag of ownRepoQuery.data.event.tags) {
          const tagName = tag[0];
          if (!['d', 'name', 'description'].includes(tagName)) {
            newTags.push(tag);
          }
        }
      } else {
        // Creating new announcement - add clone URL and relays
        // Get the clone URL from the original repo or construct one
        const cloneUrls = originalRepoQuery.data?.cloneUrls || [];
        if (cloneUrls.length > 0) {
          for (const url of cloneUrls) {
            newTags.push(['clone', url]);
          }
        } else {
          // Construct a nostr:// clone URL
          const npub = nip19.npubEncode(user.pubkey);
          newTags.push(['clone', `nostr://${npub}/${projectId}`]);
        }

        // Add relays
        const relays = originalRepoQuery.data?.relays || defaultRelays;
        for (const relay of relays) {
          newTags.push(['relays', relay]);
        }

        // Try to get earliest unique commit
        try {
          const commits = await git.log({
            dir: projectPath,
            ref: 'HEAD',
          });
          if (commits.length > 0) {
            const earliest = commits[commits.length - 1];
            newTags.push(['r', earliest.oid, 'euc']);
          }
        } catch {
          // Ignore if we can't get commits
        }
      }

      // Create the event
      const repoEvent = {
        kind: 30617,
        content: '',
        tags: newTags,
        created_at: Math.floor(Date.now() / 1000),
      };

      // Sign and publish
      const signedEvent = await user.signer.signEvent(repoEvent);
      await nostr.group(gitRelays).event(signedEvent);

      // Update git config to point to our own repo
      const naddr = nip19.naddrEncode({
        identifier: projectId,
        pubkey: user.pubkey,
        kind: 30617,
        relays: gitRelays,
      });

      await git.setConfig({
        dir: projectPath,
        path: 'nostr.repo',
        value: naddr,
      });

      return signedEvent;
    },
    onSuccess: () => {
      // Invalidate queries to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ['project-repo-own', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-repo-original', projectId] });
    },
  });

  return {
    /** The repository info (prefers user's own, falls back to original) */
    data: repoInfo,
    /** Whether data is loading */
    isLoading: ownRepoQuery.isLoading || originalRepoQuery.isLoading,
    /** Whether this project has a Nostr repository owned by the current user */
    isOwnRepo,
    /** The original repository info (from the project's git config) */
    originalRepo: originalRepoQuery.data,
    /** Update or create the repository announcement */
    updateRepo: updateMutation.mutate,
    updateRepoAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  };
}
